/**
 * Date rendering for Event Hub, in one place.
 *
 * The table and the detail page both show an event's dates, and they have to agree — a
 * row reading 2026/10/13 beside a card reading Oct 13, 2026 is the sort of split worth
 * preventing up front. YYYY/MM/DD matches the format the Figma uses on the edit form's
 * date inputs.
 */

/** Dash for an absent value, so an empty cell is never mistaken for a layout bug. */
export const NONE = '—';

/** YYYY/MM/DD in local time — the dates are whole days, not instants. */
export function formatDay(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}/${mm}/${dd}`;
}

/**
 * A date range as one string. A single-day event shows one date rather than the same one
 * twice, and an event with no start date says so — that is a real state (startDate is
 * only required at PublishEvent), not an error.
 */
export function formatRange(start: Date | undefined, end: Date | undefined): string {
  if (!start) return NONE;
  if (!end || end.getTime() === start.getTime()) return formatDay(start);
  return `${formatDay(start)} – ${formatDay(end)}`;
}

/** A timestamp, for audit fields like createdAt where the time of day matters. */
export function formatDateTime(d: Date | undefined): string {
  if (!d) return NONE;
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${formatDay(d)} ${hh}:${mi}`;
}

/**
 * A Date as the YYYY-MM-DD string Cloudscape's DatePicker expects, in LOCAL time.
 *
 * Not `toISOString().slice(0, 10)`: that converts to UTC first, so an event dated
 * 2026-10-13 in a UTC+8 timezone comes back as 2026-10-12 and the form shows the wrong
 * day the moment it loads.
 */
export function toDateInput(d: Date | undefined): string {
  return d ? formatDay(d).replace(/\//g, '-') : '';
}

/**
 * A DatePicker value back into a Date at local midnight, or undefined when empty.
 *
 * Built field-by-field rather than with `new Date(value)`, because the string form
 * "2026-10-13" is parsed as UTC midnight by spec — which lands on the previous day in any
 * timezone behind UTC, and is the mirror image of the bug in toDateInput.
 */
export function fromDateInput(value: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
